<template>
  <div
    v-click-outside="onClickOutside"
    class="customer-search-machine"
  >
    <div class="header-wrapper">
      <label class="label">
        Customer
      </label>
      <span
        v-if="showRequired"
        class="required-label"
      >(required)</span>
      <router-link
        v-if="showCreateNew"
        aria-label="Create New Customer (opens in a new window)"
        to="/customer/0"
        class="create-new"
        target="_blank"
      >
        Create
      </router-link>
    </div>
    <div class="text-input-label">
      <input
        v-model="searchTerm"
        class="text-input"
        aria-label="Search"
        :class="{'flatten-bottom': showResults}"
        placeholder="Search"
        autocomplete="off"
        :aria-required="showRequired"
        @input="onSearchTermChanged"
        @keyup.down="onDownArrowPressed"
        @keyup.up="onUpArrowPressed"
        @keyup.enter.stop="onEnterPressed"
        @keydown.tab="onClickOutside"
      >
      <div
        v-if="showResults"
        class="results-container"
      >
        <LoadingPulse
          :loading="searching"
          class="loading-spinner"
        />
        <div
          v-if="!searching"
          class="results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-option"
            :class="result.highlighted ? 'highlighted' : ''"
            @click="chooseOption(result)"
          >
            <div class="result-option-name">
              {{ `${result.firstName} ${result.lastName}` }}
            </div>
            <div class="result-option-email">
              {{ result.email }}
            </div>
            <div class="result-option-phone">
              {{ $methods.formatPhoneNumber(result.phoneNumber) }}
            </div>
          </div>
          <div
            v-if="searchResults.length == 0"
            class="no-results-container"
          >
            <div class="no-results">
              No results
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { CustomerService } from '@/services/customer.service'
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { Customer } from '@/@types/responses/customer/Customer'
import Toaster from '@meforma/vue-toaster'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'CustomerSearchMachine',
  components: {
    LoadingPulse
  },
  props: {
    showCreateNew: {
      type: Boolean,
      default: true
    },
    showRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'customer'
  ],
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function createDebounce() {
      let timeout: number|undefined = undefined
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return function (func: any, delay?: number|undefined) {
        clearTimeout(timeout)
        timeout = setTimeout(() => { func() }, delay || 500)
      }
    }
    return {
      customerService,
      debounce: createDebounce(),
      toast
    }
  },
  data() {
    return {
      searching: false,
      searchTerm: '',
      searchResults: [] as Customer[]
    }
  },
  computed: {
    showResults(): boolean {
      return this.searchTerm !== '';
    }
  },
  methods: {
    chooseOption(customer: Customer): void {
      this.searchTerm = ''
      this.$emit('customer', customer)
    },
    getHighlightedCustomer(): Customer|undefined {
      const highlightedCustomer = this.searchResults.filter(c => c.highlighted);
      return highlightedCustomer.length > 0 ? highlightedCustomer[0] : undefined;
    },
    onClickOutside(): void {
      this.searchTerm = ''
    },
    onDownArrowPressed(): void {
      const highlightedCustomer = this.getHighlightedCustomer();

      let nextIndex = highlightedCustomer ? this.searchResults.indexOf(highlightedCustomer) + 1 : 0;
      if (nextIndex > this.searchResults.length - 1)
        nextIndex = 0;
      
      if (highlightedCustomer)
        highlightedCustomer.highlighted = false;

      this.searchResults[nextIndex].highlighted = true;
    },
    onEnterPressed(): void {
      const highlightedCustomer = this.getHighlightedCustomer();
      if (highlightedCustomer)
        this.chooseOption(highlightedCustomer)
    },
    onSearchTermChanged(): void {
      if (this.searchTerm !== '') {
        this.searching = true
        this.debounce(this.searchCustomers)
      }
    },
    onUpArrowPressed(): void {
      const highlightedCustomer = this.getHighlightedCustomer();
      let previousIndex = highlightedCustomer ? this.searchResults.indexOf(highlightedCustomer) - 1 : 0;

      if (previousIndex < 0)
        previousIndex = this.searchResults.length - 1;

      if (highlightedCustomer)
        highlightedCustomer.highlighted = false;

      this.searchResults[previousIndex].highlighted = true;
    },
    searchCustomers(): void {
      this.customerService.searchCustomers(this.searchTerm, 1, 25, 'FirstName', true).then(pagingResponse => {
        this.searchResults = pagingResponse.results
        this.searching = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error(`Failed to search for your customers. Please try again.`)
        }
      })
    }
  }
})
</script>

<style scoped lang="scss">
.customer-search-machine {
  @include sac-search-machine;
}
</style>